import {createPath, Action as NavigationType, parsePath} from 'history';
import {useLocation} from 'react-router';
import {invariant} from '~/shared/utils/types';

export {NavigationType};

export type Pathname = string;
export type Search = string;
export type Hash = string;

export interface Path {
    pathname: Pathname;
    search: Search;
    hash: Hash;
}

export interface Location extends Path {
    index: number;
    state: unknown;
    key: string;
}

export type To = string | Partial<Path>;

interface Destination extends Partial<Path> {
    state?: unknown;
    key?: string;
}

interface Listener {
    (update: {action: NavigationType; location: Location}): void;
}

interface InternalState {
    usr: unknown;
    key: string;
    idx: number;
}

export function useLocationIndex() {
    const location = useLocation();
    invariant('index' in location, "Location doesn't have an index");
    return location.index as number;
}

export function createBrowserHistory() {
    const globalHistory = window.history;

    function getBrowserLocation() {
        const {pathname, search, hash} = window.location;
        const state = globalHistory.state || {};
        const location: Location = {
            pathname,
            search,
            hash,
            state: state.usr || null,
            key: state.key || 'default',
            index: state.idx,
        };
        return location;
    }

    function handlePop() {
        const nextAction = NavigationType.Pop;
        applyTx(nextAction);
    }

    window.addEventListener('popstate', handlePop);

    let action = NavigationType.Pop;
    let location = getBrowserLocation();
    const listeners = createEvents<Listener>();

    if (location.index == null) {
        location.index = 0;
        globalHistory.replaceState({...globalHistory.state, idx: location.index}, '');
    }

    function getHistoryArguments(nextLocation: Location): [data: InternalState, unused: string, url: string] {
        return [
            {
                usr: nextLocation.state,
                key: nextLocation.key,
                idx: nextLocation.index,
            },
            '',
            createHref(nextLocation),
        ];
    }

    function applyTx(nextAction: NavigationType) {
        action = nextAction;
        location = getBrowserLocation();

        listeners.notify({action, location});
    }

    const history = {
        createHref,
        get action() {
            return action;
        },
        get location() {
            return location;
        },
        push(dst: Destination) {
            const nextLocation = createNextLocation(location, dst, {indexDelta: 1});
            globalHistory.pushState(...getHistoryArguments(nextLocation));
            applyTx(NavigationType.Push);
        },
        replace(dst: Destination) {
            const nextLocation = createNextLocation(location, dst, {indexDelta: 0});
            globalHistory.replaceState(...getHistoryArguments(nextLocation));
            applyTx(NavigationType.Replace);
        },
        go(delta: number) {
            globalHistory.go(delta);
        },
        listen(listener: Listener) {
            return listeners.append(listener);
        },
    };

    return history;
}

////////////////////////////////////////////////////////////////////////////////
// UTILS
////////////////////////////////////////////////////////////////////////////////

function createEvents<F extends (...args: any[]) => void>() {
    let handlers: F[] = [];

    return {
        append(fn: F) {
            handlers.push(fn);
            return function () {
                handlers = handlers.filter(handler => handler !== fn);
            };
        },
        notify(...args: Parameters<F>) {
            handlers.forEach(fn => fn && fn(...args));
        },
    };
}

export function createHref(to: To) {
    return typeof to === 'string' ? to : createPath(to);
}

export function getDefaultLocation(): Location {
    return {
        pathname: '/',
        search: '',
        hash: '',
        state: null,
        key: 'default',
        index: 0,
    };
}

export function createNextLocation(current: Location, to: Destination, {indexDelta}: {indexDelta: 0 | 1}): Location {
    return {
        pathname: to.pathname ?? current.pathname,
        search: to.search ?? current.search,
        hash: to.hash ?? current.hash,
        state: to.state ?? null, // state defaults to `null` because `window.history.state` does
        key: to.key ?? createKey(),
        index: current.index + indexDelta,
    };
}

export function createDestination(to: To, state?: unknown): Destination {
    return {
        ...(typeof to === 'string' ? parsePath(to) : to),
        state,
    };
}

function createKey() {
    return Math.random().toString(36).substring(2, 8);
}
